import {
    MatchOfTheDayMatchListActions,
    MatchOfTheDayMatchListSetFilterDataAction,
    MatchOfTheDayMatchListSetLoadingData,
    MatchOfTheDayMatchListStoreModel,
    MatchOfTheDayMatchListGroupsOverviewTopic,
} from './match-of-the-day-match-list.constants';
import { AppDispatch, AppThunk, GetState } from '../../../../core/types';
import {
    apiSubscribeAction,
    apiUnsubscribeAction,
    attachReducerAction,
    detachReducerAction,
} from '@om-fe/core/utils/action';
import {
    getMatchOfTheDayMatchListSliceName,
    getMatchOfTheDayMatchListGroupsOverviewTopic,
} from './match-of-the-day-match-list.utils';
import { matchOfTheDayMatchListGroupsOverviewReducerCreator } from './match-of-the-day-match-list.reducer';
import { filterNullParams } from '@om-fe/core/utils/router';
import getSlice from '@om-fe/core/utils/get-slice';
import { DisciplineLiveStatus } from '@om-fe/models';

export const matchOfTheDayMatchGroupsOverviewListSetLoadingData = (
    slice: string,
    isLoadingMoreData: boolean = false,
): MatchOfTheDayMatchListSetLoadingData => ({
    type: MatchOfTheDayMatchListActions.SetLoadingData,
    meta: {
        slice,
    },
    payload: {
        isLoadingMoreData,
    },
});

export const matchOfTheDayMatchGroupsOverviewListSetFilterData = (
    slice: string, disciplineId: string, limit: number, marketGroupsOverviewIds?: string, locationId?: string, eventCategoryId?: string, liveStatus?: DisciplineLiveStatus | undefined, dateRange?: string
): MatchOfTheDayMatchListSetFilterDataAction => ({
    type: MatchOfTheDayMatchListActions.SetFilterData,
    meta: {
        slice,
    },
    payload: {
        disciplineId,
        limit,
        mainMarketsLimit: undefined,
        marketGroupsOverviewIds: marketGroupsOverviewIds,
        locationId,
        eventCategoryId,
        liveStatus,
        dateRange,
    },
});

export const getMatchOfTheDayMatchGroupsOverviewList = (
    instanceId: string,
    disciplineId: string,
    limit: number,
    marketGroupsOverviewIds?: string,
    locationId?: string,
    eventCategoryId?: string,
    liveStatus?: DisciplineLiveStatus,
    dateRange?: string,
) => (dispatch: AppDispatch, getState: GetState) => {
    const slice: string = getMatchOfTheDayMatchListSliceName(instanceId);
    const matchListState: MatchOfTheDayMatchListStoreModel | undefined = getSlice<MatchOfTheDayMatchListStoreModel>(getState(), slice);

    const topic: MatchOfTheDayMatchListGroupsOverviewTopic = getMatchOfTheDayMatchListGroupsOverviewTopic(
        disciplineId,
        matchListState?.limit || limit,
        marketGroupsOverviewIds,
        locationId,
        eventCategoryId,
        liveStatus!,
        dateRange,
    );

    dispatch(attachReducerAction(slice, matchOfTheDayMatchListGroupsOverviewReducerCreator(instanceId)));

    dispatch(matchOfTheDayMatchGroupsOverviewListSetFilterData(
        slice,
        disciplineId,
        matchListState?.limit || limit,
        marketGroupsOverviewIds,
        locationId,
        eventCategoryId,
        liveStatus,
        dateRange,
    ));

    dispatch(apiSubscribeAction(topic.path, filterNullParams(topic.params), slice));
};

export const removeMatchOfTheDayMatchGroupsOverviewList = (
    instanceId: string,
    noDetach?: boolean,
) => (dispatch: AppDispatch, getState: GetState) => {
    const slice: string = getMatchOfTheDayMatchListSliceName(instanceId);
    const matchListState: MatchOfTheDayMatchListStoreModel | undefined = getSlice<MatchOfTheDayMatchListStoreModel>(getState(), slice);

    if (!matchListState) {
        return;
    }

    const topic: MatchOfTheDayMatchListGroupsOverviewTopic = getMatchOfTheDayMatchListGroupsOverviewTopic(
        matchListState.disciplineId!,
        matchListState.limit,
        matchListState.marketGroupsOverviewIds,
        matchListState.locationId,
        matchListState.eventCategoryId,
        matchListState.liveStatus!,
        matchListState.dateRange,
    );

    if (!noDetach) {
        dispatch(detachReducerAction(slice));
    }
    dispatch(apiUnsubscribeAction(topic.path, filterNullParams(topic.params), slice));
};

export const updateMatchOfTheDayMatchGroupsOverviewList = (
    instanceId: string,
    disciplineId: string,
    limit: number,
    marketGroupsOverviewIds: string,
    locationId?: string,
    eventCategoryId?: string,
    liveStatus?: DisciplineLiveStatus,
    dateRange?: string,
) => (dispatch: AppDispatch, getState: GetState): void => {
    const slice: string = getMatchOfTheDayMatchListSliceName(instanceId);
    const matchListState: MatchOfTheDayMatchListStoreModel | undefined = getSlice<MatchOfTheDayMatchListStoreModel>(getState(), slice);

    if (!matchListState) {
        return;
    }

    const oldTopic: MatchOfTheDayMatchListGroupsOverviewTopic = getMatchOfTheDayMatchListGroupsOverviewTopic(
        matchListState.disciplineId!,
        matchListState.limit,
        matchListState.marketGroupsOverviewIds,
        matchListState.locationId,
        matchListState.eventCategoryId,
        matchListState.liveStatus!,
        matchListState.dateRange,
    );

    const newTopic: MatchOfTheDayMatchListGroupsOverviewTopic = getMatchOfTheDayMatchListGroupsOverviewTopic(
        disciplineId,
        limit,
        marketGroupsOverviewIds,
        locationId,
        eventCategoryId,
        liveStatus!,
        dateRange
    );

    dispatch(matchOfTheDayMatchGroupsOverviewListSetLoadingData(slice, true));
    dispatch(matchOfTheDayMatchGroupsOverviewListSetFilterData(
        slice,
        disciplineId,
        limit,
        marketGroupsOverviewIds,
        locationId,
        eventCategoryId,
        liveStatus,
        dateRange,
    ));

    dispatch(apiUnsubscribeAction(oldTopic.path, filterNullParams(oldTopic.params), slice));
    dispatch(apiSubscribeAction(newTopic.path, filterNullParams(newTopic.params), slice));
};

export const addMoreMatchOfTheDayMatchList = (instanceId: string, limitNumberOfEvents: number): AppThunk => (
    dispatch: AppDispatch, getState: GetState,
): void => {
    const slice: string = getMatchOfTheDayMatchListSliceName(instanceId);
    const matchListState: MatchOfTheDayMatchListStoreModel | undefined = getSlice<MatchOfTheDayMatchListStoreModel>(getState(), slice);

    if (!matchListState) {
        return;
    }

    const { limit, disciplineId, filteredMarketGroupsOverviewList, locationId, eventCategoryId, dateRange, liveStatus } = matchListState;
    const newLimit: number = limit + limitNumberOfEvents;

    if (filteredMarketGroupsOverviewList.length) {
        const groupOverviewsIds: string = filteredMarketGroupsOverviewList.join(',');
        dispatch(updateMatchOfTheDayMatchGroupsOverviewList(instanceId, disciplineId!, newLimit, groupOverviewsIds, locationId, eventCategoryId, liveStatus, dateRange));
    }
};
